/**
 * PressBtn — primary CTA button with press feedback.
 * NOTE (ADR-019): only `disabled` prop, no `loading` (YAGNI).
 * If loading is needed in future — config-object refactor required.
 * Atom purity: imports only react + tokens.
 */
import { CSSProperties, ReactNode, useState } from 'react';
import { P, T } from '../../styles/tokens';

export interface PressBtnProps {
  children: ReactNode;
  onClick?: () => void;
  bg?: string;
  fg?: string;
  full?: boolean;
  size?: 'lg' | 'md' | 'sm';
  disabled?: boolean;
  variant?: 'solid' | 'ghost';
  style?: CSSProperties;
}

const HEIGHT: Record<'lg' | 'md' | 'sm', number> = { lg: 52, md: 44, sm: 34 };
const FONT_SIZE: Record<'lg' | 'md' | 'sm', number> = { lg: 17, md: 16, sm: 14 };

export function PressBtn({
  children,
  onClick,
  bg = P.terracotta,
  fg = '#ffffff',
  full = true,
  size = 'lg',
  disabled = false,
  variant = 'solid',
  style,
}: PressBtnProps) {
  const [down, setDown] = useState(false);

  const h = HEIGHT[size];
  const fs = FONT_SIZE[size];
  const isGhost = variant === 'ghost';

  const isTerra = bg === P.terracotta || bg === P.terraLight;
  // Translucent secondary tint (Назад, Изменить — bg="rgba(45,37,24,0.08)"):
  // the heavy primary drop-shadow floated free of the near-invisible fill
  // ("тень оторвалась"). Give it a subtle shadow that hugs the button instead.
  const isTinted = typeof bg === 'string' && bg.startsWith('rgba');
  let shadow: string;
  if (disabled || isGhost) {
    shadow = 'none';
  } else if (isTinted) {
    shadow = '0 1px 2px rgba(45,37,24,0.06)';
  } else if (isTerra) {
    shadow = '0 6px 16px rgba(192,81,58,0.25), inset 0 1px 0 rgba(255,255,255,0.20)';
  } else {
    shadow = '0 4px 14px rgba(25,23,23,0.14), inset 0 1px 0 rgba(255,255,255,0.08)';
  }

  const base: CSSProperties = {
    height: h,
    borderRadius: h / 2,
    background: isGhost ? 'transparent' : bg,
    color: fg,
    border: isGhost ? `1.5px solid ${fg}` : 'none',
    fontFamily: T.fontBody,
    fontSize: fs,
    fontWeight: 600,
    padding: '0 22px',
    width: full ? '100%' : 'auto',
    cursor: disabled ? 'default' : 'pointer',
    opacity: disabled ? 0.5 : down ? 0.72 : 1,
    pointerEvents: disabled ? 'none' : 'auto',
    transition: 'opacity 0.15s ease',
    boxShadow: shadow,
    letterSpacing: -0.2,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    boxSizing: 'border-box',
    ...style,
  };

  return (
    <button
      onClick={disabled ? undefined : onClick}
      onPointerDown={() => setDown(true)}
      onPointerUp={() => setDown(false)}
      onPointerLeave={() => setDown(false)}
      disabled={disabled}
      style={base}
    >
      {children}
    </button>
  );
}
